/**
 * Attribute validation schemas.
 * Defines attribute types, constraints, and display configuration.
 */
import { z } from "zod";
// ============================================================================
// Common Patterns
// ============================================================================
/** Currency code pattern (ISO 4217) */
const currencyCodePattern = /^[A-Z]{3}$/;
// ============================================================================
// Attribute Types
// ============================================================================
/** All supported attribute types */
export const AttributeTypeSchema = z.enum([
    "string",
    "number",
    "boolean",
    "enum",
    "date",
    "currency",
    "percentage",
    "url",
    "email",
    "phone",
    "array",
]);
// ============================================================================
// Comparison Operators
// ============================================================================
/** Comparison operators for eligibility and ranking rules */
export const ComparisonOperatorSchema = z.enum([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "in",
    "not_in",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "between",
    "is_null",
    "is_not_null",
    "regex",
    "exists",
]);
/** Aggregation operators for array attributes */
export const AggregationOperatorSchema = z.enum(["any", "all", "none", "count"]);
// ============================================================================
// Attribute Constraints
// ============================================================================
/** String attribute constraints */
export const StringConstraintsSchema = z
    .object({
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(1).optional(),
    pattern: z.string().optional(),
    format: z.enum(["text", "multiline", "rich"]).optional(),
})
    .strict();
/** Number attribute constraints */
export const NumberConstraintsSchema = z
    .object({
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
    integer: z.boolean().optional(),
})
    .strict();
/** Enum option definition */
export const EnumOptionSchema = z
    .object({
    value: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
})
    .strict();
/** Enum attribute constraints */
export const EnumConstraintsSchema = z
    .object({
    options: z.array(EnumOptionSchema).min(1),
    allowMultiple: z.boolean().optional(),
})
    .strict();
/** Date attribute constraints */
export const DateConstraintsSchema = z
    .object({
    minDate: z.string().datetime().optional(),
    maxDate: z.string().datetime().optional(),
    format: z.enum(["date", "datetime", "time"]).optional(),
})
    .strict();
/** Currency attribute constraints */
export const CurrencyConstraintsSchema = z
    .object({
    currency: z.string().regex(currencyCodePattern, "Invalid ISO 4217 currency code"),
    min: z.number().optional(),
    max: z.number().optional(),
    precision: z.number().int().min(0).max(4).optional(),
})
    .strict();
/** Percentage attribute constraints */
export const PercentageConstraintsSchema = z
    .object({
    min: z.number().min(0).max(100).optional(),
    max: z.number().min(0).max(100).optional(),
    precision: z.number().int().min(0).max(4).optional(),
})
    .strict();
/** URL attribute constraints */
export const UrlConstraintsSchema = z
    .object({
    protocols: z.array(z.string()).optional(),
    requireHttps: z.boolean().optional(),
})
    .strict();
/** Empty constraints for boolean, email, phone */
export const EmptyConstraintsSchema = z.object({}).strict();
/** Array attribute constraints schema */
export const ArrayConstraintsSchema = z
    .object({
    itemType: AttributeTypeSchema,
    minItems: z.number().int().min(0).optional(),
    maxItems: z.number().int().min(1).optional(),
    uniqueItems: z.boolean().optional(),
})
    .strict();
export const AttributeConstraintsSchema = z.union([
    StringConstraintsSchema,
    NumberConstraintsSchema,
    EnumConstraintsSchema,
    DateConstraintsSchema,
    CurrencyConstraintsSchema,
    PercentageConstraintsSchema,
    UrlConstraintsSchema,
    ArrayConstraintsSchema,
    EmptyConstraintsSchema,
]);
// ============================================================================
// Attribute Display
// ============================================================================
/** Attribute display configuration */
export const AttributeDisplaySchema = z
    .object({
    format: z.string().optional(),
    prefix: z.string().optional(),
    suffix: z.string().optional(),
    showInCard: z.boolean().optional(),
    showInDetails: z.boolean().optional(),
    showInComparison: z.boolean().optional(),
    priority: z.number().int().min(0).optional(),
    group: z.string().optional(),
    icon: z.string().optional(),
})
    .strict();
// ============================================================================
// Attribute Definition
// ============================================================================
/** Base attribute definition (without refinements) */
const BaseAttributeDefinitionSchema = z
    .object({
    key: z
        .string()
        .min(1)
        .regex(/^[a-z][a-zA-Z0-9]*$/, "Key must be camelCase"),
    type: AttributeTypeSchema,
    label: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    required: z.boolean(),
    constraints: AttributeConstraintsSchema.optional(),
    display: AttributeDisplaySchema.optional(),
    defaultValue: z.unknown().optional(),
    computed: z.boolean().optional(),
    computeExpression: z.string().optional(),
})
    .strict();
/** Complete attribute definition in a schema */
export const AttributeDefinitionSchema = BaseAttributeDefinitionSchema.refine((data) => {
    // If computed is true, computeExpression must be provided
    if (data.computed === true && !data.computeExpression) {
        return false;
    }
    return true;
}, {
    message: "computeExpression is required when computed is true",
    path: ["computeExpression"],
});
/** Derived attribute computed from other attributes */
export const DerivedAttributeDefinitionSchema = BaseAttributeDefinitionSchema.extend({
    computed: z.literal(true),
    computeExpression: z.string().min(1),
    dependencies: z.array(z.string()).min(1),
}).strict();
// ============================================================================
// Attribute Values
// ============================================================================
/** String attribute value */
export const StringAttributeValueSchema = z
    .object({
    type: z.literal("string"),
    value: z.string(),
})
    .strict();
/** Number attribute value */
export const NumberAttributeValueSchema = z
    .object({
    type: z.literal("number"),
    value: z.number(),
})
    .strict();
/** Boolean attribute value */
export const BooleanAttributeValueSchema = z
    .object({
    type: z.literal("boolean"),
    value: z.boolean(),
})
    .strict();
/** Enum attribute value */
export const EnumAttributeValueSchema = z
    .object({
    type: z.literal("enum"),
    value: z.string(),
})
    .strict();
/** Date attribute value */
export const DateAttributeValueSchema = z
    .object({
    type: z.literal("date"),
    value: z.string().datetime(),
})
    .strict();
/** Currency attribute value */
export const CurrencyAttributeValueSchema = z
    .object({
    type: z.literal("currency"),
    value: z.number(),
    currency: z.string().regex(currencyCodePattern),
})
    .strict();
/** Percentage attribute value */
export const PercentageAttributeValueSchema = z
    .object({
    type: z.literal("percentage"),
    value: z.number().min(0).max(100),
})
    .strict();
/** URL attribute value */
export const UrlAttributeValueSchema = z
    .object({
    type: z.literal("url"),
    value: z.string().url(),
})
    .strict();
/** Email attribute value */
export const EmailAttributeValueSchema = z
    .object({
    type: z.literal("email"),
    value: z.string().email(),
})
    .strict();
/** Phone attribute value */
export const PhoneAttributeValueSchema = z
    .object({
    type: z.literal("phone"),
    value: z.string().min(1),
})
    .strict();
/** Array attribute value schema */
export const ArrayAttributeValueSchema = z.lazy(() => z
    .object({
    type: z.literal("array"),
    value: z.array(AttributeValueSchema),
})
    .strict());
/** Typed attribute value union schema */
export const AttributeValueSchema = z.lazy(() => z.union([
    StringAttributeValueSchema,
    NumberAttributeValueSchema,
    BooleanAttributeValueSchema,
    EnumAttributeValueSchema,
    DateAttributeValueSchema,
    CurrencyAttributeValueSchema,
    PercentageAttributeValueSchema,
    UrlAttributeValueSchema,
    EmailAttributeValueSchema,
    PhoneAttributeValueSchema,
    ArrayAttributeValueSchema,
]));
/** Raw attribute value (before type wrapping) */
export const RawAttributeValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.array(z.number()),
    z.array(z.boolean()),
]);
// ============================================================================
// Vertical Schema
// ============================================================================
/** Vertical schema definition */
export const VerticalSchemaDefinitionSchema = z
    .object({
    verticalKey: z
        .string()
        .min(1)
        .regex(/^[a-z][a-z0-9-]*$/, "Vertical key must be kebab-case"),
    version: z.number().int().min(1),
    userInputs: z.array(AttributeDefinitionSchema),
    productAttributes: z.array(AttributeDefinitionSchema),
    derivedAttributes: z.array(DerivedAttributeDefinitionSchema).optional(),
})
    .strict();
// ============================================================================
// Display Format Config
// ============================================================================
/** Display format configuration */
export const DisplayFormatConfigSchema = z
    .object({
    locale: z.string().min(2).max(10),
    currency: z.string().regex(currencyCodePattern).optional(),
    dateFormat: z.string().optional(),
    numberFormat: z
        .object({
        style: z.enum(["decimal", "currency", "percent", "unit"]).optional(),
        minimumFractionDigits: z.number().int().min(0).max(20).optional(),
        maximumFractionDigits: z.number().int().min(0).max(20).optional(),
        useGrouping: z.boolean().optional(),
    })
        .passthrough()
        .optional(),
})
    .strict();
